/**
 * Bedienung zu Projekten auf /runs (Entwurf B „Projekte als Reiter“, Canvas
 * „99takes Runs-Filter“, gewählt von Robert am 03.11.2026). Regeln in
 * `runs-projekte.ts`.
 *
 *   - Ab 1024 px eine Spalte links mit „Alle“, den Projekten und „Ohne
 *     Projekt“, je mit der Zahl der Runs. Darunter wird sie zu einer
 *     Pillenreihe, die man seitlich wischt.
 *   - Über der Liste eine Suche (Studie, Projekt, Text, Zielgruppe).
 *   - Unter „Ohne Projekt“ ein Kasten, in dem man Studien zuordnet; dasselbe
 *     geht in Claude mit `projekt`.
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ApiFehler, setzeStudienProjekt } from './api';
import { t } from './sprache';
import type { ProjektUebersicht, ProjektWahl } from './runs-projekte';

const gleich = (a: ProjektWahl, b: ProjektWahl) =>
  a.art === b.art && (a.art !== 'projekt' || (b.art === 'projekt' && a.name === b.name));

function Eintrag({ an, onClick, children, zahl }: { an: boolean; onClick: () => void; children: ReactNode; zahl: number }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={an}
      className={`flex min-h-11 shrink-0 cursor-pointer items-center justify-between gap-3 rounded-full border px-4 text-left text-[0.9375rem] transition-colors duration-150
        lg:w-full lg:rounded-feld lg:border-transparent lg:px-3.5
        ${an ? 'border-knopf bg-knopf font-semibold text-auf-marke lg:border-knopf' : 'border-linie-feld bg-flaeche text-tinte hover:border-linie-stark lg:bg-transparent lg:hover:bg-ebene'}`}
    >
      <span className="max-w-[14rem] truncate">{children}</span>
      <span className={`text-[0.8125rem] tabular-nums ${an ? '' : 'text-text-hell'}`}>{zahl}</span>
    </button>
  );
}

export function ProjektNavigation({ u, wahl, waehle }: { u: ProjektUebersicht; wahl: ProjektWahl; waehle: (w: ProjektWahl) => void }) {
  const eintraege: Array<{ w: ProjektWahl; name: string; zahl: number }> = [
    { w: { art: 'alle' }, name: t('Alle', 'All'), zahl: u.alle },
    ...u.projekte.map(p => ({ w: { art: 'projekt', name: p.name } as ProjektWahl, name: p.name, zahl: p.runs })),
    ...(u.ohne > 0 ? [{ w: { art: 'ohne' } as ProjektWahl, name: t('Ohne Projekt', 'No project'), zahl: u.ohne }] : []),
  ];
  // Am Telefon liegt die gewählte Pille sonst rechts außerhalb (gesehen bei
  // 340 px mit „Ohne Projekt“): die Reihe rückt sie ins Bild, ohne die Seite
  // senkrecht zu verschieben.
  const reihe = useRef<HTMLDivElement>(null);
  const schluessel = wahl.art === 'projekt' ? wahl.name : wahl.art;
  useEffect(() => {
    const r = reihe.current;
    const an = r?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!r || !an || r.scrollWidth <= r.clientWidth) return;
    const links = an.offsetLeft; // die Reihe ist offsetParent (relative)
    if (links < r.scrollLeft || links + an.offsetWidth > r.scrollLeft + r.clientWidth) r.scrollLeft = Math.max(0, links - 16);
  }, [schluessel, u.projekte.length]);
  return (
    <nav aria-label={t('Projekte', 'Projects')} className="min-w-0 lg:w-[14rem] lg:shrink-0">
      <div className="mb-2 hidden px-3.5 text-[0.75rem] font-semibold tracking-[0.08em] text-text-hell uppercase lg:block">{t('Projekte', 'Projects')}</div>
      <div ref={reihe} className="relative -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:px-0 lg:flex-col lg:gap-0.5 lg:overflow-visible">
        {eintraege.map(e => (
          <Eintrag key={e.w.art + e.name} an={gleich(e.w, wahl)} onClick={() => waehle(e.w)} zahl={e.zahl}>{e.name}</Eintrag>
        ))}
      </div>
    </nav>
  );
}

export function RunSuche({ wert, setzen, platzhalter }: { wert: string; setzen: (s: string) => void; platzhalter: string }) {
  const id = useId();
  return (
    <div className="flex min-h-12 items-center gap-2.5 rounded-feld border border-linie-feld bg-flaeche px-3.5 focus-within:border-marke focus-within:ring-2 focus-within:ring-marke/30">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden className="shrink-0 text-text-hell"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
      <label htmlFor={id} className="sr-only">{t('Runs durchsuchen', 'Search runs')}</label>
      <input
        id={id}
        type="search"
        value={wert}
        onChange={e => setzen(e.target.value)}
        placeholder={platzhalter}
        className="min-w-0 flex-1 border-0 bg-transparent py-2 text-[0.9375rem] text-tinte outline-none placeholder:text-text-hell"
      />
    </div>
  );
}

/** Eine Studie ohne Projekt: Feld mit Vorschlägen und Knopf. */
function StudieZuordnen({ studie, projekte, fertig }: { studie: { id: string; name: string }; projekte: string[]; fertig: () => void }) {
  const [wert, setWert] = useState('');
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState('');
  const id = useId();
  const liste = `${id}-liste`;
  const speichern = async () => {
    if (!wert.trim()) return;
    setArbeitet(true);
    setFehler('');
    try {
      await setzeStudienProjekt(studie.id, wert.trim());
      fertig();
    } catch (e) {
      setFehler((e as ApiFehler).message);
    } finally {
      setArbeitet(false);
    }
  };
  return (
    <li className="flex flex-col gap-2 border-t border-linie-zart px-4 py-3.5 first:border-t-0 sm:flex-row sm:items-center sm:gap-4 sm:px-5">
      <label htmlFor={id} className="min-w-0 flex-1 text-[0.9375rem] font-medium break-words text-tinte">{studie.name}</label>
      <form className="flex gap-2" onSubmit={e => { e.preventDefault(); void speichern(); }}>
        <input
          id={id}
          list={liste}
          value={wert}
          onChange={e => setWert(e.target.value)}
          maxLength={80}
          placeholder={t('Projekt', 'Project')}
          className="min-h-11 w-full min-w-0 rounded-feld border border-linie-feld bg-flaeche px-3 text-[0.9375rem] text-tinte outline-none focus:border-marke focus:ring-2 focus:ring-marke/30 sm:w-[12rem]"
        />
        <datalist id={liste}>{projekte.map(p => <option key={p} value={p} />)}</datalist>
        <button type="submit" disabled={arbeitet || !wert.trim()} className="min-h-11 shrink-0 cursor-pointer rounded-full border-0 bg-knopf px-4 text-[0.875rem] font-semibold text-auf-marke hover:bg-knopf-hover disabled:cursor-default disabled:opacity-50">
          {arbeitet ? t('Speichert …', 'Saving …') : t('Zuordnen', 'Assign')}
        </button>
      </form>
      {fehler && <p className="m-0 text-[0.8125rem] text-schlecht sm:basis-full">{fehler}</p>}
    </li>
  );
}

export function ProjekteZuordnen({ studien, projekte, fertig }: { studien: Array<{ id: string; name: string }>; projekte: string[]; fertig: () => void }) {
  return (
    <section className="rounded-karte bg-ton-blau">
      <div className="px-4 pt-4 pb-3 sm:px-5">
        <h3 className="m-0 text-[0.9375rem] font-semibold text-tinte">{t('Studien einem Projekt zuordnen', 'Assign studies to a project')}</h3>
        <p className="mt-1 mb-0 text-[0.875rem] leading-relaxed text-text">
          {t('Ein Run gehört zum Projekt seiner Studie. In Claude geht es auch: „Leg die Studie unter dem Projekt Everto ab.“ Runs ohne Studie bleiben hier.',
            'A run belongs to the project of its study. You can also tell Claude: “File this study under the project Everto.” Runs without a study stay here.')}
        </p>
      </div>
      {studien.length > 0 && (
        <ul className="m-0 list-none rounded-b-karte bg-flaeche p-0">
          {studien.map(s => <StudieZuordnen key={s.id} studie={s} projekte={projekte} fertig={fertig} />)}
        </ul>
      )}
    </section>
  );
}
